import React, { useEffect, useMemo } from "react";
import { Button, Drawer, Form, Input, InputNumber, Space, Switch } from "antd";
import GooglePlacesAutocompleteInput from "components/common/GooglePlacesAutocompleteInput";
import useAppConfig from "hooks/access-control/useAppConfig";
import { addressFieldsFromPlaceDetails, regionSummaryFromPlaceDetails } from "utils/googlePlaces";

export default function PopularLocationDrawer({
  open,
  onClose,
  onSubmit,
  loading,
  initialValues,
}) {
  const [form] = Form.useForm();
  const { appConfig } = useAppConfig();
  const googleMapsApiKey = appConfig?.googleMapsApiKey || appConfig?.googleMapsApiMatrixKey || "";
  const isUpdate = Boolean(initialValues?.id);

  const initial = useMemo(
    () => ({
      label: initialValues?.label || "",
      city: initialValues?.city || "",
      county: initialValues?.county || "",
      state: initialValues?.state || "",
      country: initialValues?.country || "",
      placeId: initialValues?.placeId || "",
      searchCount: initialValues?.searchCount ?? 0,
      ranking: initialValues?.ranking ?? 0,
      active: initialValues?.active ?? true,
    }),
    [initialValues],
  );

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initial);
    } else {
      form.resetFields();
    }
  }, [form, initial, open]);

  const handlePlaceResolved = async ({ details }) => {
    const fields = addressFieldsFromPlaceDetails(details);
    const label = regionSummaryFromPlaceDetails(details);
    form.setFieldsValue({
      label,
      city: fields.city || "",
      county: fields.county || "",
      state: fields.state || "",
      country: fields.country || "",
      placeId: fields.placeId || "",
    });
  };

  const handleFinish = async (values) => {
    await onSubmit({
      ...values,
      searchCount: values.searchCount ?? 0,
      ranking: values.ranking ?? 0,
      active: values.active ?? true,
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      destroyOnClose
      placement="right"
      width={520}
      zIndex={2000}
      title={isUpdate ? "Edit Popular Location" : "Add Popular Location"}
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={loading} onClick={() => form.submit()}>
            {isUpdate ? "Update" : "Save"}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFinish} requiredMark={false}>
        <Form.Item
          name="label"
          label="Location"
          rules={[{ required: true, message: "Location is required" }]}
        >
          <GooglePlacesAutocompleteInput
            googleMapsApiKey={googleMapsApiKey}
            placeholder="Search for a location..."
            types={["geocode", "establishment"]}
            onResolvePlace={handlePlaceResolved}
          />
        </Form.Item>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="city" label="City">
            <Input placeholder="Auto-filled" autoComplete="off" readOnly />
          </Form.Item>
          <Form.Item name="county" label="County">
            <Input placeholder="Auto-filled" autoComplete="off" readOnly />
          </Form.Item>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="state" label="State">
            <Input placeholder="Auto-filled" autoComplete="off" readOnly />
          </Form.Item>
          <Form.Item name="country" label="Country">
            <Input placeholder="Auto-filled" autoComplete="off" readOnly />
          </Form.Item>
        </div>

        <Form.Item name="placeId" label="Google Place ID">
          <Input placeholder="Auto-filled" autoComplete="off" readOnly />
        </Form.Item>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Form.Item name="searchCount" label="Search Count">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="ranking" label="Ranking">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>
      </Form>
    </Drawer>
  );
}
